import { MenuItem, Text } from "@chakra-ui/react";
import _ from "lodash";
import React from "react";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { asyncForEach } from "src/services/asyncHelpers";
import { maybePluralize } from "src/services/format";
import { OfferError } from "../errors";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canClearOfferExpiration } from "../validations/clearOfferExpiration";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Canceling auto-revoke will prevent this offer from being automatically revoked.",
    "Canceling auto-revoke will prevent these offers from being automatically revoked."
  );

const BULK_CLEAR_OFFER_EXPIRATION_MAX_INPUT_SIZE = 100;

export const ClearOfferExpiration = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Cancel auto-revoke",
  isBulkAction = false,
}: Props) => {
  const toast = useAvelaToast();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, cancel auto-revoke",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = React.useCallback(
    async (evt: React.MouseEvent) => {
      const schoolForms = selectedFormSchoolStatuses;
      const count = schoolForms.length;
      const trackArgs = {
        actionType: "CLEAR_AUTO_REVOKE" as const,
        bulk: isBulkAction,
        count,
        async: false,
      };

      const offerNoun = maybePluralize(count, "offer");
      setHeader(<Text>{`Cancel auto-revoke for ${count} ${offerNoun}?`}</Text>);
      setBody(<Text>{confirmationBodyText(count)}</Text>);

      trackFormAction({ verb: "Clicked", ...trackArgs });

      if (await confirm()) {
        trackFormAction({ verb: "Confirmed", ...trackArgs });

        const loadingToastId = toast({
          title: `Working hard`,
          description: `Canceling auto-revoke for ${count} ${offerNoun}...`,
          status: "loading",
          duration: null,
        });

        try {
          const formSchoolPairs: Array<{
            form_id: string;
            school_id: string;
          }> = schoolForms.map((result) => {
            return {
              form_id: result.form_id,
              school_id: result.school_id,
            };
          });

          await asyncForEach(
            _.chunk(
              formSchoolPairs,
              BULK_CLEAR_OFFER_EXPIRATION_MAX_INPUT_SIZE
            ),
            (chunk) =>
              FSR.bulkSetOfferExpiration({
                body: {
                  form_school_pairs: chunk,
                  expires_at: null,
                },
              })
          );

          if (onRefetch) onRefetch();

          toast.close(loadingToastId);
          toast({
            description: `Canceled auto-revoke for ${count} ${offerNoun}`,
            status: "info",
          });
          trackFormAction({ verb: "Completed", ...trackArgs });
        } catch (err: unknown) {
          trackFormActionError(err, trackArgs);
          toast.close(loadingToastId);
          let message = "Check your network and try again.";

          if (err instanceof OfferError) {
            message = err.message;
          }

          toast.error({
            title: "Oops! Something went wrong!",
            description: message,
          });
          console.error(err);
        }
      } else {
        trackFormAction({ verb: "Canceled", ...trackArgs });
      }
    },
    [
      confirm,
      isBulkAction,
      onRefetch,
      selectedFormSchoolStatuses,
      setBody,
      setHeader,
      toast,
    ]
  );

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canClearOfferExpiration(
            selectedFormSchoolStatuses,
            selectedFormSchools
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
